import React, { memo, useState, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import {
  useStandardsTableProps,
  useStandardsConfigureColumns,
} from "StandardsTable/hooks";
import _ from "lodash";
import ACLStore from "lib/aclStore";
import getCurriculumIdsFilterForTags from "Utils/getCurriculumIdsFilterForTags";
import { useI18n } from "Hooks";
import useFetchQuery from "Hooks/useFetchQuery";

// modules
import {
  getLearningCourseStandardsQuery,
  getOrganizationSubjectsGradesAndTagsQuery,
  getPublishedSubjectStandardDetailsQuery,
} from "../../modules/StandardsSelectionModalQueries";
import {
  getSelectedCurriculumId,
  getOrganizationId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";

// design system
import {
  Dropdown,
  ButtonDisclosure,
  Badge,
  SearchInput,
} from "@toddle-edu/ds-web";
import { FilterOutlined } from "@toddle-edu/ds-icons";

// components
import Loading from "UIComponents/Loading";
import { default as StandardsDataGrid } from "StandardsTable";
import ConfigureColumns from "../ConfigureColumns";
import FiltersOverlay from "UIComponents/FiltersOverlay";

// utils
import {
  getPublishedSubjectStandardQueryVariables,
  getStandardEdges,
  getAllFiltersCount,
  getAppliedFiltersCountForFilter,
  getFilterOverlayOptions,
} from "../../modules/Utils";
import getCheckboxValue from "StandardsTable/modules/cellUtils/getCheckboxValue";
import { handleSelectionChange } from "StandardsTable/modules/Utils";
import { getAcademicSetupType } from "Teacher/routes/LearningCourses/modules/Utils";

// constants
import {
  DATA_SET_COLUMNS_SETTING,
  ENABLE_STANDARDS_OUTLINE,
} from "Constants/permissionConstants";
import {
  DEFAULT_STANDARD_FILTERS,
  DATA_GRID_COLUMNS,
  NON_CONFIGURABLE_COLUMNS,
  DEFAULT_HIDDEN_COLUMNS,
  SEARCH_LABELS,
  DATA_SCOPE_ENUM,
} from "../../modules/Constants";
import {
  DATA_GRID_CELL_TYPES,
  DATA_GRID_COLUMN_TYPES,
} from "StandardsTable/modules/constants";
import { SUBJECT_BASED_GRADING } from "Constants/stringConstants";

// scss
import classes from "./StandardsTable.scss";

const TOOLTIP_MAP = {
  NONE: "classRoom:standard_does_not_have_grading_enabled",
  VERTICAL: "classRoom:standard_rolled_up_from_its_child_standards",
};

// override TAGGED_IN_ASSIGNMENTS column to show assessment count, default one shows assignment count
const getColumnProperties = t => {
  return {
    [DATA_GRID_COLUMN_TYPES.TAGGED_IN_ASSIGNMENTS]: {
      headerConfig: {
        title: t("scopeAndSequence:tagged_in_label", {
          label: t("common:task_plural"),
        }),
        width: 176,
        tooltip: t("scopeAndSequence:tagged_in_label_header_tooltip", {
          label: t("common:task_plural"),
        }),
      },
      cellConfig: {
        cellType: DATA_GRID_CELL_TYPES.NUMBER,
        styles: {
          align: "right",
          color: "#008392",
        },
      },
      customConfig: {
        property: "standardCountWrapper.assessment.totalCount",
        showLoader: true,
      },
    },
  };
};

const _getEdgesFromNodeMap = params => {
  const {
    t,
    edges,
    nodeMap,
    selectedStandards,
    isParentTaggingEnabled,
  } = params;

  return Array.from(nodeMap.values(), value => {
    const { rowId, children, columns } = value || {};

    const rowData = _.find(edges, ({ node }) => node?.id === rowId)?.node;
    const { gradingType } = rowData || {};

    if (!isParentTaggingEnabled && !_.isEmpty(children)) {
      columns[0].isDisabled = true;
    }

    if (gradingType !== "INDIVIDUAL") {
      const tooltipText = t(TOOLTIP_MAP[gradingType]);

      _.forEach(columns, column => {
        column.isDisabled = true;
        column.tooltip = tooltipText;
      });
    }

    columns[0].value = getCheckboxValue({
      value: selectedStandards.has(rowId),
    });

    return {
      rowId,
      children,
      columns,
    };
  });
};

const StandardsTable = memo(props => {
  const {
    selectedStandards,
    setSelectedStandards,
    selectedStandardsDataScope,
    initialSelectedStandards,
    onChangeStandardsDataScope,
    academicSetupType,
    selectedStandardSet,
    learningCourseId,
    curriculumProgramType,
    orgId,
    curriculumProgramId,
    unitTaggedStandardIds,
  } = props;

  const allColumns = useMemo(() => {
    let columns = DATA_GRID_COLUMNS;

    // Conditions for Standards 2.0 with Subject Based Grading
    if (academicSetupType === SUBJECT_BASED_GRADING) {
      columns = _.without(columns, DATA_GRID_COLUMN_TYPES.SUBJECT_AREAS);
    }

    // Conditions for Old Standards
    if (!academicSetupType) {
      columns = _.without(
        columns,
        DATA_GRID_COLUMN_TYPES.POWER_STANDARD,
        DATA_GRID_COLUMN_TYPES.GRADING_TYPE,
        DATA_GRID_COLUMN_TYPES.SUBJECT_AREAS,
        DATA_GRID_COLUMN_TYPES.TAGGED_IN_COURSES,
        DATA_GRID_COLUMN_TYPES.TAGGED_IN_ASSIGNMENTS,
        DATA_GRID_COLUMN_TYPES.TAGGED_IN_PORTFOLIO,
        DATA_GRID_COLUMN_TYPES.TAGGED_IN_UNITS
      );
    }

    return columns;
  }, [academicSetupType]);

  const { t } = useI18n();

  const [searchText, setSearchText] = useState("");

  const [isFilterOverlayOpen, setIsFilterOverlayOpen] = useState(false);
  const [standardFilters, setStandardFilters] = useState(
    DEFAULT_STANDARD_FILTERS
  );

  // Fetch published subject standard details
  const {
    data: publishedStandardData,
    loading: isPublishedStandardLoading,
  } = useFetchQuery({
    query: getPublishedSubjectStandardDetailsQuery,
    variables: getPublishedSubjectStandardQueryVariables({
      id: selectedStandardSet?.id,
      searchText,
      standardFilters,
      curriculumProgramType,
    }),
    skipCondition:
      !selectedStandardSet?.id ||
      selectedStandardsDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE,
    fetchPolicy: "cache-and-network",
  });

  // Fetch learning course standards
  const {
    data: learningCourseData,
    loading: isLearningCourseLoading,
  } = useFetchQuery({
    query: getLearningCourseStandardsQuery,
    variables: getPublishedSubjectStandardQueryVariables({
      id: learningCourseId,
      standardSetIds: selectedStandardSet?.id ? [selectedStandardSet.id] : [],
      searchText,
      standardFilters,
      curriculumProgramType,
    }),
    skipCondition:
      !learningCourseId ||
      selectedStandardsDataScope !== DATA_SCOPE_ENUM.LEARNING_COURSE,
    fetchPolicy: "cache-and-network",
  });

  // Fetch organization subjects, grades and tags
  const { data: organizationData } = useFetchQuery({
    query: getOrganizationSubjectsGradesAndTagsQuery,
    variables: {
      orgId,
      curriculumProgramId,
      genericTagsFilter: {
        types: ["GENERIC"],
        ...getCurriculumIdsFilterForTags({ curriculumProgramId }),
      },
    },
    skipCondition: !orgId || !curriculumProgramId,
    fetchPolicy: "cache-and-network",
  });

  // Derive edges based on which query is active
  const node =
    selectedStandardsDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE
      ? _.get(learningCourseData, "node")
      : _.get(publishedStandardData, "node");
  const allEdges = getStandardEdges({ node, selectedStandardsDataScope });
  const edges =
    selectedStandardsDataScope === DATA_SCOPE_ENUM.UNIT
      ? _.filter(allEdges, edge =>
          _.includes(unitTaggedStandardIds, edge?.node?.id)
        )
      : allEdges;
  const isData = !_.isEmpty(node);
  const isLoading =
    selectedStandardsDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE
      ? isLearningCourseLoading
      : isPublishedStandardLoading;

  // Derive subjects, grades and tags
  const subjects = _.get(organizationData, "node.subjects", []);
  const grades = _.get(organizationData, "node.grades", []);
  const tags = _.get(organizationData, "node.genericTagsV2", []);

  const filterOverlayOptions = getFilterOverlayOptions({
    t,
    grades,
    subjects,
    tags,
  });

  const onApplyFilterOverlay = useCallback(({ filters }) => {
    setStandardFilters(filters);
    setIsFilterOverlayOpen(false);
  }, []);

  const onCloseFilterOverlay = useCallback(
    () => setIsFilterOverlayOpen(false),
    []
  );

  const appliedFiltersCount = useMemo(
    () => getAllFiltersCount({ currentFilters: standardFilters }),
    [standardFilters]
  );

  const customResetFilters = useCallback(() => {
    setStandardFilters(DEFAULT_STANDARD_FILTERS);
    setSearchText("");
  }, [setSearchText]);

  // if initialSelectedStandards are not part of course then toggle showCourseStandards to false
  useEffect(() => {
    if (
      (selectedStandardsDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE ||
        selectedStandardsDataScope === DATA_SCOPE_ENUM.UNIT) &&
      initialSelectedStandards
    ) {
      const allStandardIds = _.map(edges, edge => edge?.node?.id);
      const isEveryStandardSelected = _.every(
        initialSelectedStandards,
        standard => _.includes(allStandardIds, standard)
      );

      if (!isEveryStandardSelected) {
        onChangeStandardsDataScope({ key: DATA_SCOPE_ENUM.ALL });
      }
    }
  }, [
    edges,
    initialSelectedStandards,
    onChangeStandardsDataScope,
    selectedStandardsDataScope,
  ]);

  const {
    columnIdsForConfigureModal,
    currentColumns,
    updateColumns,
  } = useStandardsConfigureColumns({
    settingName: DATA_SET_COLUMNS_SETTING,
    moduleName: "StandardBasedRubric",
    defaultColumns: allColumns,
    nonConfigurableColumns: NON_CONFIGURABLE_COLUMNS,
    defaultHiddenColumns: DEFAULT_HIDDEN_COLUMNS,
  });

  const { columns, nodeMap, properties, sideNavData } = useStandardsTableProps({
    edges,
    columns: currentColumns,
    properties: getColumnProperties(t),
  });

  const getEdgesFromNodeMap = useCallback(
    nodeMap => {
      return _getEdgesFromNodeMap({
        t,
        edges,
        nodeMap,
        selectedStandards: new Set(selectedStandards),
        isParentTaggingEnabled:
          academicSetupType ||
          ACLStore.can("FeatureFlag:EnableParentStandardsTagging"),
      });
    },
    [t, academicSetupType, selectedStandards, edges]
  );

  const onCellEdit = useCallback(
    ({ params }) => {
      return handleSelectionChange({
        params,
        selectedRows: selectedStandards,
        setSelectedRows: rows => {
          const selectedRows = Array.from(rows);
          setSelectedStandards(selectedRows);
        },
      });
    },
    [selectedStandards, setSelectedStandards]
  );

  const searchLabel = SEARCH_LABELS["STANDARDS_SELECTION"];

  const showOutline = ACLStore.can(ENABLE_STANDARDS_OUTLINE);

  const gridClasses = classNames(classes.gridBody, {
    [classes.outlineHiddenGridBody]: !showOutline,
  });

  return (
    <StandardsDataGrid
      columns={columns}
      nodeMap={nodeMap}
      properties={properties}
      sideNavData={sideNavData}
      showLoader={isLoading}
      showOutline={showOutline}
    >
      <StandardsDataGrid.Body bodyClass={gridClasses}>
        <StandardsDataGrid.Header>
          <header className={classes.headerContainer}>
            <div className={classes.headerLeftContainer}>
              {/* search is common for both steps */}
              <SearchInput
                value={searchText}
                placeholder={t("common:search_for_a_with_label", {
                  label: t(searchLabel),
                })}
                size={"medium"}
                onChange={setSearchText}
                focusSearchBySlash={true}
              />
            </div>
            <div className={classes.headerRightContainer}>
              <ConfigureColumns
                currentColumns={currentColumns}
                updateColumns={updateColumns}
                columnIdsForConfigureModal={columnIdsForConfigureModal}
                properties={properties}
              />

              {!_.isEmpty(filterOverlayOptions) ? (
                <Dropdown
                  overlay={
                    <FiltersOverlay
                      options={filterOverlayOptions}
                      currentFilters={standardFilters} // state
                      getAllFiltersCount={getAllFiltersCount}
                      getAppliedFiltersCountForFilter={
                        getAppliedFiltersCountForFilter
                      }
                      onApply={onApplyFilterOverlay}
                      handleClose={onCloseFilterOverlay}
                      defaultFilters={DEFAULT_STANDARD_FILTERS}
                      customContainerClass={classes.filterOverlayContainer}
                    />
                  }
                  visible={isFilterOverlayOpen}
                  onVisibleChange={setIsFilterOverlayOpen}
                >
                  <ButtonDisclosure
                    icon={<FilterOutlined />}
                    type="outlined"
                    variant="neutral"
                  >
                    {appliedFiltersCount > 0 ? (
                      <span className={classes.filterBtn}>
                        <Badge
                          value={appliedFiltersCount}
                          type="numeric"
                          variant="dark"
                          shape="circle"
                        />
                      </span>
                    ) : null}
                  </ButtonDisclosure>
                </Dropdown>
              ) : null}
            </div>
          </header>
        </StandardsDataGrid.Header>
        {isLoading && !isData ? (
          <Loading />
        ) : (
          <StandardsDataGrid.Grid
            resetFilters={customResetFilters}
            getEdgesFromNodeMap={getEdgesFromNodeMap}
            onCellEdit={onCellEdit}
            freezeColumns={2}
          />
        )}
      </StandardsDataGrid.Body>
    </StandardsDataGrid>
  );
});

StandardsTable.displayName = "StandardsTable";

const mapStateToProps = state => {
  const orgId = getOrganizationId();
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumProgramType = getCurrentCurriculumProgramType(state);
  const academicSetupType = getAcademicSetupType();

  return {
    orgId,
    curriculumProgramId,
    curriculumProgramType,
    academicSetupType,
  };
};

export default connect(mapStateToProps, null)(StandardsTable);
